/**
 * PronunciationSheet — §4.7's lexicon, and the one screen that has to be honest about a limitation.
 *
 * ── Why this UI is spelling-shaped, and why it says so ──────────────────────
 *
 * **Chatterbox has no SSML, no phoneme input and no prosody markup.** Verified on the live API twice.
 * There is no field to put `/ʃɪˈvɔːn/` in, because there is no channel to send it down. So the only
 * pronunciation fix that exists is *spell it differently for the synthesiser* — the page keeps saying
 * `Siobhan` and `sentences.ttsText` carries `Shiv-awn`.
 *
 * That is not a detail to bury. Somebody who does not know the fix is spelling-shaped types `/saɪ/`
 * into the box and hears the voice read out slashes, decides the feature is broken, and never comes
 * back. So the sheet says it in one plain sentence at the top, and the field is labelled *"Spell it
 * the way it sounds"* rather than *"Pronunciation"*.
 *
 * ── Nothing is spent without a number in front of it ───────────────────────
 *
 * Every keystroke that produces a valid entry asks the server what saving it would do, and the answer
 * is rendered by `describeImpact()` **from `shared/`** — *"affects 7 books · 214 sentences · 4 min of
 * GPU"* — so the sentence on the button and the sentence in the server's audit row are one string,
 * not two that drift. The Save button never appears without it.
 *
 * ── LAW 13 ─────────────────────────────────────────────────────────────────
 *
 * The "Everywhere" scope is offered from `isGod(actor)` in `shared/access.ts` — the same predicate the
 * route enforces with, never a prop, never a build flag. books shipped a button badged *"Needs voice
 * capture — Phase 6"* that stayed bolted shut against a feature that had landed in Phase 6, because
 * the claim was a `disabled` prop rather than a check.
 *
 * ── Styling ────────────────────────────────────────────────────────────────
 *
 * This file owns no stylesheet. `reader-page.css` already carries `.ab-sheet`, `.ab-field`,
 * `.ab-choices`, `.ab-choice`, `.ab-btn`, `.ab-notice` and `.ab-sr`, and they are reused rather than
 * re-cut; the handful of rules that are genuinely new are inline objects with token colours, so both
 * themes keep working and nothing is added to a file another session is editing.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactElement } from "react";

import type { Actor } from "../../../shared/actor.ts";
import { isGod } from "../../../shared/access.ts";
import {
  describeImpact,
  EMPTY_IMPACT,
  gpuLabel,
  LEXICON_LIMITS,
  validateEntry,
  type LexiconImpact,
  type LexiconProblem,
  type PronunciationScope,
} from "../../../shared/narration/lexicon.ts";
import { ApiError, api } from "../lib/api.ts";

// ─── what the routes answer with ─────────────────────────────────────────────

export interface LexiconRow {
  id: string;
  scope: PronunciationScope;
  bookId: string | null;
  pattern: string;
  replacement: string;
  /** Sentences the entry changed when it was applied. `pronunciations.hits`. */
  hits: number;
  createdAt: number;
}

export interface ImpactResponse {
  applied: boolean;
  impact: LexiconImpact;
  summary: string;
  sampleOf: number;
  changes: { sid: string; pageId: string | null; text: string; before: string; after: string; shadowed: boolean }[];
}

interface ListResponse {
  bookId: string;
  entries: LexiconRow[];
  merged: Record<string, string>;
  pending: { impact: LexiconImpact; summary: string } | null;
}

export interface PronunciationSheetProps {
  open: boolean;
  bookId: string;
  /** The actor, for LAW 13. Guests never see this sheet; a signed-in non-god sees only "This book". */
  actor: Actor | null;
  /** The word the reader selected, if the sentence menu opened this. Prefills the printed form. */
  initialPattern?: string;
  /**
   * The canonical page the reader is standing on. Sent with the write so **that one page** is
   * re-narrated at priority 0 — the person is about to press play and hear the fix. Nothing else is
   * enqueued; the rest of the book is narrated the ordinary way, when somebody opens it.
   */
  pageId?: string | null;
  editionId?: string | null;
  voiceId?: string | null;
  onClose(): void;
  /** Fired after a successful write or removal, with the diff, so the reader can refresh its plan. */
  onApplied?: (result: ImpactResponse & { job: { id: string } | null }) => void;
}

// ─── layout ──────────────────────────────────────────────────────────────────

const body: CSSProperties = {
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-3)",
};

const explain: CSSProperties = {
  margin: 0,
  fontSize: "var(--text-xs)",
  lineHeight: "var(--leading-prose, 1.6)",
  color: "var(--read-ink-muted)",
};

const problemStyle: CSSProperties = {
  margin: 0,
  fontSize: "var(--text-xs)",
  lineHeight: 1.5,
  color: "var(--warn, var(--read-link))",
};

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--space-2)",
  paddingBlock: "var(--space-2)",
  borderTop: "1px solid var(--read-rule)",
  fontSize: "var(--text-sm)",
};

const inputStyle: CSSProperties = {
  width: "100%",
  minHeight: "var(--tap-min)",
  padding: "0 var(--space-3)",
  border: "1px solid var(--read-rule)",
  borderRadius: "var(--radius)",
  background: "var(--read-bg)",
  color: "var(--read-ink)",
  font: "inherit",
  // 16px minimum on iOS: anything smaller and Safari zooms the whole viewport on focus, which on a
  // `position: fixed` reader leaves the page scrolled sideways with no way back. The same note is on
  // `input` in app.css; it is repeated because this control does not inherit that rule.
  fontSize: "max(16px, var(--text-base))",
};

/** How long after the last keystroke the impact is asked for. */
export const IMPACT_DEBOUNCE_MS = 600;

// ─── the sheet ───────────────────────────────────────────────────────────────

export function PronunciationSheet(props: PronunciationSheetProps): ReactElement | null {
  const { open, bookId, actor, initialPattern = "", pageId = null, editionId = null, voiceId = null, onClose, onApplied } = props;

  const [pattern, setPattern] = useState(initialPattern);
  const [replacement, setReplacement] = useState("");
  const [scope, setScope] = useState<PronunciationScope>("book");
  const [list, setList] = useState<ListResponse | null>(null);
  const [impact, setImpact] = useState<ImpactResponse | null>(null);
  const [pricing, setPricing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [removing, setRemoving] = useState<{ row: LexiconRow; summary: string } | null>(null);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusTo = useRef<Element | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrl = useRef<string | null>(null);

  const canPromote = isGod(actor);
  const problem: LexiconProblem | null = useMemo(
    () => (pattern === "" && replacement === "" ? null : validateEntry({ pattern, replacement })),
    [pattern, replacement],
  );
  const ready = problem === null && pattern !== "" && replacement !== "";

  // ── open / close ──
  useEffect(() => {
    if (!open) return;
    restoreFocusTo.current = document.activeElement;
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      (restoreFocusTo.current as HTMLElement | null)?.focus?.();
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open) setPattern(initialPattern);
  }, [open, initialPattern]);

  // Revoke the blob when the sheet goes away. A preview is a few hundred KB and a reader who
  // auditions twenty spellings holds twenty of them until the tab closes otherwise.
  useEffect(
    () => () => {
      if (objectUrl.current !== null) URL.revokeObjectURL(objectUrl.current);
    },
    [],
  );

  const reload = useCallback(async () => {
    try {
      setList(await api.get<ListResponse>(`/pronunciations?bookId=${encodeURIComponent(bookId)}`));
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : "Couldn't load this book's pronunciations.");
    }
  }, [bookId]);

  useEffect(() => {
    if (open) void reload();
  }, [open, reload]);

  // ── the price, debounced ──
  useEffect(() => {
    if (!open || !ready) {
      setImpact(null);
      return;
    }
    let cancelled = false;
    setPricing(true);
    const timer = setTimeout(() => {
      api
        .post<ImpactResponse>("/pronunciations/impact", { scope, bookId: scope === "book" ? bookId : null, pattern, replacement })
        .then((res) => {
          if (!cancelled) setImpact(res);
        })
        .catch((e: unknown) => {
          if (!cancelled) setNotice(e instanceof ApiError ? e.message : "Couldn't work out what that would change.");
        })
        .finally(() => {
          if (!cancelled) setPricing(false);
        });
    }, IMPACT_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, ready, scope, bookId, pattern, replacement]);

  if (!open) return null;

  /**
   * Hear it. **Metered** — `AB_PREVIEW_HOUR`, enforced server-side — so this is a button and never a
   * side effect of typing. One short clip is about 1.4 s on a GPU shared with four other services.
   *
   * A raw `fetch` rather than `api.post`, because the answer is `audio/wav` and `api` exists to turn a
   * body into JSON or a typed refusal. The refusal path is still `ApiError`'s shape by hand, so a 429
   * reads the same here as everywhere else.
   */
  const hearIt = async (): Promise<void> => {
    if (!ready || previewing) return;
    setPreviewing(true);
    setNotice(null);
    try {
      const res = await fetch("/api/v1/pronunciations/preview", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ bookId, voiceId, pattern, replacement }),
      });
      if (!res.ok) {
        const problemBody = (await res.json().catch(() => null)) as { error?: string } | null;
        setNotice(problemBody?.error ?? "The voice service didn't answer.");
        return;
      }
      const blob = await res.blob();
      if (objectUrl.current !== null) URL.revokeObjectURL(objectUrl.current);
      objectUrl.current = URL.createObjectURL(blob);
      const el = audioRef.current;
      if (el !== null) {
        el.src = objectUrl.current;
        await el.play().catch(() => {
          // A browser that refuses to play without a gesture has already had one — this IS the
          // gesture. If it still refuses, the control below is a real <audio> the reader can press.
        });
      }
    } catch {
      setNotice("Couldn't reach the server to hear that.");
    } finally {
      setPreviewing(false);
    }
  };

  const save = async (): Promise<void> => {
    if (!ready || saving) return;
    setSaving(true);
    setNotice(null);
    try {
      const res = await api.post<ImpactResponse & { job: { id: string } | null }>("/pronunciations", {
        scope,
        bookId: scope === "book" ? bookId : null,
        pattern,
        replacement,
        pageId,
        editionId,
        voiceId,
      });
      setReplacement("");
      setImpact(null);
      await reload();
      onApplied?.(res);
      setNotice(saved(res));
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : "Couldn't save that.");
    } finally {
      setSaving(false);
    }
  };

  /** Removal is priced first, for the same reason saving is: it is the same amount of work undone. */
  const askRemove = async (row: LexiconRow): Promise<void> => {
    try {
      const res = await api.get<ImpactResponse>(`/pronunciations/${row.id}/impact`);
      setRemoving({ row, summary: res.summary });
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : "Couldn't work out what removing that would change.");
    }
  };

  const confirmRemove = async (): Promise<void> => {
    if (removing === null) return;
    try {
      const res = await api.del<ImpactResponse & { job: { id: string } | null }>(`/pronunciations/${removing.row.id}`);
      setRemoving(null);
      await reload();
      onApplied?.(res);
      setNotice(`Removed. ${describeImpact(res.impact)}.`);
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : "Couldn't remove that.");
    }
  };

  const pending = list?.pending ?? null;

  return (
    <>
      <div className="ab-drawer-scrim" onClick={onClose} aria-hidden="true" />
      <div
        className="ab-sheet"
        role="dialog"
        aria-label="How this book says a word"
        ref={panelRef}
        tabIndex={-1}
        data-no-turn=""
      >
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flex: "0 0 auto" }}>
          <strong style={{ flex: "1 1 auto", fontSize: "var(--text-sm)", color: "var(--read-ink)" }}>
            How it says a word
          </strong>
          <button type="button" className="ab-btn" onClick={onClose} aria-label="Close">
            Done
          </button>
        </div>

        <div style={body}>
          {/*
            The honest sentence. It is first because it explains the shape of every field below it:
            there is no phonetic input on this model, so the fix is a respelling.
          */}
          <p style={explain}>
            The voice has no way to be given a pronunciation — so the fix is to <strong>spell the word
            differently just for the voice</strong>. The page still shows the real word; only what the
            voice is handed changes.
          </p>

          <div className="ab-field">
            <label className="ab-field-label" htmlFor="ab-pron-pattern">
              As it is printed
            </label>
            <input
              id="ab-pron-pattern"
              style={inputStyle}
              value={pattern}
              maxLength={LEXICON_LIMITS.patternMax}
              spellCheck={false}
              autoCapitalize="none"
              onChange={(e) => setPattern(e.target.value)}
              placeholder="Siobhan"
            />
          </div>

          <div className="ab-field">
            <label className="ab-field-label" htmlFor="ab-pron-replacement">
              Spell it the way it sounds
            </label>
            <input
              id="ab-pron-replacement"
              style={inputStyle}
              value={replacement}
              maxLength={LEXICON_LIMITS.replacementMax}
              spellCheck={false}
              autoCapitalize="none"
              onChange={(e) => setReplacement(e.target.value)}
              placeholder="Shiv-awn"
            />
          </div>

          {problem !== null ? (
            <p style={problemStyle} role="alert">
              {problem.message}
            </p>
          ) : null}

          {/* Scope. LAW 13: the second choice exists only for an actor `isGod()` says is one. */}
          <div className="ab-field">
            <span className="ab-field-label">Where it applies</span>
            <div className="ab-choices">
              <button
                type="button"
                className="ab-choice"
                aria-pressed={scope === "book"}
                onClick={() => setScope("book")}
              >
                This book
              </button>
              {canPromote ? (
                <button
                  type="button"
                  className="ab-choice"
                  aria-pressed={scope === "global"}
                  onClick={() => setScope("global")}
                >
                  Every book
                </button>
              ) : null}
            </div>
          </div>

          <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
            <button type="button" className="ab-btn" onClick={() => void hearIt()} disabled={!ready || previewing}>
              {previewing ? "Listening…" : "Hear it"}
            </button>
            <button type="button" className="ab-btn" onClick={() => void save()} disabled={!ready || saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>

          {/*
            The price. `describeImpact` lives in `shared/` and is the same string the server puts in
            its audit row, so the number on the button and the number in the log cannot drift.
            While it is still being computed the line says so rather than showing a stale figure —
            a number that lags the field is worse than no number, because it is believed.
          */}
          <p style={explain} aria-live="polite">
            {!ready
              ? "Type both, and this line will say what saving it would change."
              : pricing || impact === null
                ? "Working out what that would change…"
                : capitalise(describeImpact(impact.impact))}
            {impact !== null && impact.impact.shadowed > 0 ? (
              <>
                {" "}
                <span style={{ opacity: 0.8 }}>
                  ({impact.impact.shadowed} of them {impact.impact.shadowed === 1 ? "has" : "have"} their own
                  respelling already, so {impact.impact.shadowed === 1 ? "it costs" : "they cost"} nothing.)
                </span>
              </>
            ) : null}
          </p>

          {/* The diff, which is what makes the count believable. A sample, and it says so. */}
          {impact !== null && impact.changes.length > 0 ? (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {impact.changes.slice(0, 5).map((c) => (
                <li key={`${c.sid}:${c.after}`} style={{ ...rowStyle, display: "block" }}>
                  <span style={{ color: "var(--read-ink-muted)", fontSize: "var(--text-xs)" }}>{c.text}</span>
                  <br />
                  <span style={{ color: "var(--read-ink)" }}>{c.after}</span>
                </li>
              ))}
              {impact.sampleOf > impact.changes.length ? (
                <li style={{ ...rowStyle, color: "var(--read-ink-muted)", fontSize: "var(--text-xs)" }}>
                  …and {impact.sampleOf - impact.changes.length} more.
                </li>
              ) : null}
            </ul>
          ) : null}

          {/*
            The ingest gap, surfaced rather than hidden. `sentences.ttsText` is written at ingest with
            an empty overrides bag, so a book uploaded after an entry was saved — or re-imported —
            starts life spelled the old way. One button closes it.
          */}
          {pending !== null && pending.impact.sentences > 0 ? (
            <div className="ab-notice">
              <span style={{ flex: "1 1 auto" }}>
                This book was imported before some of these were saved — {pending.impact.sentences}{" "}
                {pending.impact.sentences === 1 ? "sentence still says" : "sentences still say"} it the old way
                {pending.impact.estGpuSec > 0 ? ` (${gpuLabel(pending.impact.estGpuSec)} of GPU to fix)` : ""}.
              </span>
              <button
                type="button"
                className="ab-btn"
                onClick={() => {
                  void api
                    .post<ImpactResponse>("/pronunciations/renormalize", { bookId, apply: true })
                    .then(async (res) => {
                      await reload();
                      setNotice(`Applied. ${describeImpact(res.impact)}.`);
                    })
                    .catch((e: unknown) => setNotice(e instanceof ApiError ? e.message : "Couldn't apply them."));
                }}
              >
                Apply
              </button>
            </div>
          ) : null}

          {/* What is already saved. Per-book first — it is the one that wins a tie. */}
          {list !== null && list.entries.length > 0 ? (
            <div className="ab-field">
              <span className="ab-field-label">Saved</span>
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {[...list.entries]
                  .sort((a, b) => (a.scope === b.scope ? 0 : a.scope === "book" ? -1 : 1))
                  .map((row) => (
                    <li key={row.id} style={rowStyle}>
                      <span style={{ flex: "1 1 auto", minWidth: 0 }}>
                        <span style={{ color: "var(--read-ink)" }}>{row.pattern}</span>
                        <span aria-hidden="true" style={{ opacity: 0.5, paddingInline: "0.45em" }}>
                          →
                        </span>
                        <span style={{ color: "var(--read-ink)" }}>
                          {row.replacement === "" ? "(not read aloud)" : row.replacement}
                        </span>
                        <span style={{ display: "block", fontSize: "var(--text-2xs)", color: "var(--read-ink-muted)" }}>
                          {row.scope === "book" ? "this book" : "every book"}
                          {row.hits > 0 ? ` · ${row.hits} ${row.hits === 1 ? "sentence" : "sentences"}` : ""}
                        </span>
                      </span>
                      <button type="button" className="ab-btn" onClick={() => void askRemove(row)}>
                        Remove
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
          ) : null}

          {/*
            Removal is confirmed with its own price, in the same words as saving. It is not a
            destructive-looking dialog for drama: putting a spelling back is the same amount of GPU as
            changing it, unless the old clips are still cached — which the number already accounts for.
          */}
          {removing !== null ? (
            <div className="ab-notice" role="alertdialog" aria-label="Remove this pronunciation?">
              <span style={{ flex: "1 1 auto" }}>
                Remove <strong>{removing.row.pattern}</strong> → {removing.row.replacement || "(not read aloud)"}?{" "}
                {capitalise(removing.summary)}.
              </span>
              <button type="button" className="ab-btn" onClick={() => void confirmRemove()}>
                Remove
              </button>
              <button type="button" className="ab-btn" onClick={() => setRemoving(null)}>
                Keep
              </button>
            </div>
          ) : null}

          {notice !== null ? (
            <p style={explain} role="status">
              {notice}
            </p>
          ) : null}

          {/*
            A real <audio> element rather than `new Audio()`: on iOS a programmatic element created
            outside a gesture can be refused, and a visible control gives the reader something to press
            when it is. `preload="none"` — the src is a blob that only exists after Hear it.
          */}
          <audio ref={audioRef} preload="none" controls={false} aria-label="The preview" />
        </div>
      </div>
    </>
  );
}

/** "affects 2 books · …" → "Affects 2 books · …". The shared copy is a fragment; this is a sentence. */
function capitalise(s: string): string {
  return s === "" ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * What the toast says after a save, and it is deliberately not "Saved."
 *
 * The interesting half is what happens next: the pages that were narrated are no longer current, and
 * the one the reader is standing on has been queued at priority 0. Saying so is the difference between
 * a fix that appears to have done nothing and a fix with a visible consequence.
 */
export function saved(res: ImpactResponse & { job: { id: string } | null }): string {
  if (res.impact.sentences === 0) return "Saved — no sentence in this book uses that spelling yet.";
  const parts = [`Saved. ${capitalise(describeImpact(res.impact))}`];
  if (res.impact.pagesNarrated > 0) {
    parts.push(
      res.job !== null
        ? "This page is being read again now; the others will be when you reach them."
        : "Those pages will be read again when you reach them.",
    );
  }
  return `${parts.join(". ")}.`;
}

/** For a caller that wants the shape before the first request answers. */
export const NO_IMPACT: LexiconImpact = EMPTY_IMPACT;

export default PronunciationSheet;
